import Address from '@enview/interface/types/Address';
import { CommitteeMembership } from '@enview/interface/types/Committee';
import { ElectedPosition, Person } from '@enview/interface/types/Person';
import { faFacebook, faXTwitter, faYoutube } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from 'i18next';
import { ReactElement } from 'react';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  trackOpenPersonSourceLink,
  trackPersonContactClicked,
} from '../../analytics/PersonAnalytics';
import Hideable from '../../components/Hideable';
import ChevronIcon from '../../components/svg/chevron_big_up';
import { g as abilityGlossary } from '../../config/ability';
import { checkPermission } from '../../dux';
import { State } from '../../dux/@types';
import { getCommitteeName } from '../../helpers/CommitteesHelper';
import PersonImage from '../Shared/PersonImage';
import GateTooltip, { GateTooltipFeature } from '../Shared/Tooltips/TooltipGate';
import React from 'react';

type PersonOverviewProps = {
  person: Person;
};

type DetailProps = {
  label: string;
  value: string;
  isTitle: boolean;
  hrefValue?: string;
  onClick?: () => void;
};

// TODO: Move this component to shared location
const Detail = (props: DetailProps): ReactElement => {
  const { label, value, isTitle, hrefValue, onClick } = props;
  return (
    <div className="detail">
      <h3 className="label" key="label">
        {label.toUpperCase()}
      </h3>
      <div className={`value${isTitle ? ' titleSize' : ''}`} key="value">
        {hrefValue ? (
          <a href={hrefValue} onClick={onClick} target="_blank">
            {value}
          </a>
        ) : (
          value
        )}
      </div>
    </div>
  );
};

Detail.defaultProps = {
  isTitle: false,
};

const PersonDetails = (props: PersonOverviewProps): ReactElement => {
  const { person } = props;
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const electedPosition =
    person.electedPositions && person.electedPositions.find((ep) => ep.isActive);

  const dispatch = useDispatch();
  const canViewCommittee = () => {
    return dispatch(
      checkPermission(abilityGlossary.VIEW, abilityGlossary.COMMITTEE, true),
    );
  };

  const contactInfoTitle = (
    <h2 className="hideable-content-title">
      <span>Contact Info</span>
      <span className="float-right arrow-icon">
        <ChevronIcon />
      </span>
    </h2>
  );

  const committeesTitle = (
    <h2 className="hideable-content-title">
      <span>Committees</span>
      <span className="float-right arrow-icon">
        <ChevronIcon />
      </span>
    </h2>
  );

  const getCommitteeDetail = (
    committeeMembership: CommitteeMembership,
  ): ReactElement | undefined => {
    const { committee, role } = committeeMembership;
    return (
      electedPosition && (
        <div className="detail" key={committeeMembership.id}>
          <span className="value" id={`committees-${committeeMembership.id}`}>
            <Link
              aria-disabled={!canViewCommittee()}
              to={canViewCommittee() ? `/committee/${committee.id}` : '#'}
            >
              {getCommitteeName(committee, electedPosition.chamber)}
            </Link>
          </span>
          {!canViewCommittee() && (
            <GateTooltip
              accountType="pro"
              anchorId={`committees-${committeeMembership.id}`}
              className="committee-tooltip"
              customContent={
                <Trans
                  components={{
                    committeeLink: (
                      <a
                        className="d-inline"
                        href={t('urls.legislatorDetails')}
                        target="_blank"
                      />
                    ),
                    gate: <p className="tooltip-header" />,
                  }}
                  i18nKey="featureGating.tooltipText.committeesInfo"
                  key="featureGating.tooltipText.committeesInfo"
                />
              }
              featureName={GateTooltipFeature.Committees}
              place="bottom"
            />
          )}
          <p className="label">{role}</p>
        </div>
      )
    );
  };

  const getEmailLink = (position: ElectedPosition): string => {
    return position.email?.toLocaleLowerCase().startsWith('http')
      ? position.email
      : `mailto:${position.email}`;
  };

  const getSourceLink = (url: string): string => {
    return url.includes('kslegislature.org/li/b') && url.slice(-1) !== '/'
      ? `${url}/`
      : url;
  };

  return (
    <React.Fragment key={person.id}>
      <div
        className="person-details flag d-flex flex-column"
        key="person-details-container"
      >
        <div className="detail-group">
          <PersonImage person={person} />
          <h1 className="title" key="title">
            {person.name}
          </h1>
        </div>

        {electedPosition && (
          <>
            <div className="detail-group">
              <Detail key="current-title" label="title" value={electedPosition.role} />
              <Detail
                key="state"
                label="state"
                value={electedPosition.jurisdiction.name}
              />
              <Detail
                key="chamber"
                label="chamber"
                value={
                  electedPosition.chamber
                    ? electedPosition.chamber.name
                    : 'Not Available'
                }
              />
              <Detail
                key="district"
                label="district"
                value={electedPosition.district}
              />
              <Detail key="party" label="party" value={electedPosition.party} />
              {person.gender && (
                <Detail key="gender" label="gender" value={person.gender} />
              )}
            </div>

            {/* Contact info - collapsible section */}
            <div
              className="person-hideable-content"
              style={{ borderBottom: 'none', marginTop: '20px' }}
            >
              <Hideable expanded title={contactInfoTitle}>
                {electedPosition.email && (
                  <Detail
                    hrefValue={getEmailLink(electedPosition)}
                    key="email"
                    label="email"
                    onClick={() =>
                      trackPersonContactClicked(
                        false,
                        electedPosition.email!,
                        person,
                        orgUser,
                      )
                    }
                    value={electedPosition.email}
                  />
                )}
                {person.legislativeAddresses &&
                  person.legislativeAddresses.map((address, index) => {
                    const description = address.title ? `${address.title} ` : '';
                    const parsedAddress = Address.formatAddressToString(address);
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <div key={index}>
                        {address.phone && (
                          <Detail
                            hrefValue={`tel:${address.phone}`}
                            key={`${description
                              .toLowerCase()
                              .replace(/ /g, '-')}-phone`}
                            label={`${description} phone`}
                            onClick={() =>
                              trackPersonContactClicked(
                                true,
                                address.phone!,
                                person,
                                orgUser,
                              )
                            }
                            value={address.phone}
                          />
                        )}
                        {parsedAddress && (
                          <Detail
                            key={`${description
                              .toLowerCase()
                              .replace(/ /g, '-')}-address`}
                            label={`${description} address`}
                            value={parsedAddress}
                          />
                        )}
                      </div>
                    );
                  })}
                <div className="social" key="social">
                  {electedPosition.twitter && (
                    <FontAwesomeIcon
                      className="brand-icons"
                      icon={faXTwitter}
                      onClick={() =>
                        window.open(
                          `https://www.twitter.com/${electedPosition.twitter}`,
                        )
                      }
                    />
                  )}
                  {electedPosition.facebook && (
                    <FontAwesomeIcon
                      className="brand-icons"
                      icon={faFacebook}
                      onClick={() =>
                        window.open(
                          `https://www.facebook.com/${electedPosition.facebook}`,
                        )
                      }
                    />
                  )}
                  {electedPosition.youtube && (
                    <FontAwesomeIcon
                      className="brand-icons"
                      icon={faYoutube}
                      onClick={() =>
                        window.open(
                          `https://www.youtube.com/c/${electedPosition.youtube}`,
                        )
                      }
                    />
                  )}
                </div>
              </Hideable>
            </div>

            {/* Committees info - collapsible section */}
            {electedPosition.committeeMemberships &&
              electedPosition.committeeMemberships.length > 0 && (
                <div className="person-hideable-content">
                  <Hideable expanded title={committeesTitle}>
                    {electedPosition.committeeMemberships.map((committeeMembership) => (
                      <React.Fragment key={committeeMembership.id}>
                        {getCommitteeDetail(committeeMembership)}
                      </React.Fragment>
                    ))}
                  </Hideable>
                </div>
              )}

            <div className="detail-group">
              <div className="sources" key="sources">
                {electedPosition.sources.map((source) => (
                  <a
                    className="source"
                    href={getSourceLink(source.url)}
                    key={source.url}
                    onClick={() =>
                      trackOpenPersonSourceLink(source.url, person, orgUser)
                    }
                  >
                    Source
                  </a>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </React.Fragment>
  );
};

export default PersonDetails;
